import { logError, logInfo } from '../utilities/logging';
import { safeJsonParse } from '../utilities/safeJsonParse';
import { CartItem } from './addToCart';
import { fetchRetry } from './fetch';
import { getCart } from './getCart';

const SHOPIFY_STORE_DOMAIN = window.shopUrl;

/**
 * Add the full image urls to custom line item properties.
 * @param {Object} options
 * @param {Object} options.newPayloads - The new payloads to add.
 * @param {() => void} [options.onRetryStart] - Callback when retry starts.
 * @param {() => void} [options.onRetryEnd] - Callback when retry ends.
 * @returns {Promise<[{cartHtml: string, cartItems: CartItem[]}]>} - Array of updated cart items.
 */
export const addImageUrls = async ({ onRetryStart, onRetryEnd, newPayloads, checkId }) => {
  const resolvedItems: { cartHtml: string | false; cartItems: CartItem[] }[] = await Promise.all(
    newPayloads.map(async (newPayload) => {
      try {
        const response = await fetchRetry({
          url: `${SHOPIFY_STORE_DOMAIN}/cart/change.js`,
          options: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...newPayload,
              properties: {
                ...newPayload.properties,
                _personalisation_base_attributes: JSON.stringify(
                  newPayload.properties?._personalisation_base_attributes
                )
              },
              sections: 'cart-drawer'
            })
          },
          retryDelay: 1000,
          retries: 15,
          onRetryStart,
          onRetryEnd,
          extraShouldRetryCheck: ({ cartData }) => {
            const item = cartData.items.find((item) => item.properties?._check_id === checkId);
            return Object.keys(item?.properties || {}) !== Object.keys(newPayload.properties);
          }
        });

        if (!response?.ok) {
          const errorText = (await response?.text()) || 'Failed to add image urls to cart';
          const status = response?.status || 0;

          // Handle "no valid id or line parameter" 400 - this often means
          // the cart change was already processed or the line item was already updated
          // Check the cart to verify and return cart data if the item is already there
          const parsedError = safeJsonParse({ jsonString: errorText });
          if (status === 400 && parsedError?.message === 'no valid id or line parameter') {
            logInfo({
              label: 'addImageUrls',
              message:
                'Cart change returned 400 with "no valid id or line parameter" - checking cart for existing data',
              extraData: { newPayload, statusCode: status, errorText }
            });

            // Check if the item is already in the cart with the expected updates
            const cartData = await getCart();
            if (!('error' in cartData)) {
              const existingItem = cartData.items.find((item) => item.properties?._check_id === checkId);
              if (existingItem) {
                logInfo({
                  label: 'addImageUrls',
                  message: 'Item found in cart - treating 400 as success',
                  extraData: { checkId, existingItem }
                });
                return Promise.resolve({
                  cartHtml: false,
                  cartItems: cartData.items as unknown as CartItem[]
                });
              }
            }

            // Item not found in cart - this is a real error
            logError({
              label: 'addImageUrls',
              error: new Error(`Cart change failed and item not found in cart`),
              message: `Cart change returned 400 and item with checkId ${checkId} not found in cart`,
              extraData: { newPayload, statusCode: status, errorText, checkId }
            });
            throw new Error(`Cart change failed with status ${status}: ${errorText}`);
          }

          logError({
            label: 'addImageUrls',
            error: new Error(`Cart change failed with status ${status}: ${errorText}`),
            message: `Cart change failed with status ${status}: ${errorText}`,
            extraData: { newPayload, response, statusCode: status, errorText }
          });
          throw new Error(`Cart change failed with status ${status}: ${errorText}`);
        }
        const data = await response.json();
        return Promise.resolve({ cartHtml: data.sections?.['cart-drawer'] || false, cartItems: data.items || [] });
      } catch (error) {
        logError({
          label: 'addImageUrls',
          error,
          message: 'Failed to update cart',
          extraData: { newPayload }
        });
        throw error;
      }
    })
  );

  const lastItem = resolvedItems.filter((item: { cartHtml: string | false }) => item.cartHtml !== false).at(-1);

  return {
    cartHtml: lastItem?.cartHtml || false,
    cartItems: lastItem?.cartItems || ([] as CartItem[])
  };
};
